/**
 * "One final step: open their Passport."
 *
 * The consent moment for a learner under 18. Two steps, in this order and no
 * other: confirm an adult is here, then take explicit, itemised consent. The
 * database refuses assessment writes until both are done, so this screen is the
 * only way in for a minor — and it is written to feel like a beginning, not a
 * legal hurdle.
 */
import { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ShieldCheck,
  Mail,
  Loader2,
  ArrowRight,
  Lock,
  CheckCircle2,
  Sparkles,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import WeKitLogo from "@/components/WeKitLogo";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { useConsentState, CONSENT_RETENTION_YEARS } from "@/lib/consentGate";
import { ga4Once } from "@/lib/ga4";

type Step = "verify" | "code" | "consent" | "done";

const PassportConsent = () => {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const next = params.get("next") || "/start";
  const { user } = useAuth();
  const { state, loading, refresh } = useConsentState();

  const [step, setStep] = useState<Step>("verify");
  const [guardianEmail, setGuardianEmail] = useState("");
  const [guardianName, setGuardianName] = useState("");
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [declarations, setDeclarations] = useState({
    guardian: false,
    notice: false,
    processing: false,
  });

  const childName = state.childName?.trim() || "your child";

  useEffect(() => {
    if (!loading && state.isMinor && !state.allowed) ga4Once("parent_consent_started", { source: "passport-consent" });
  }, [loading, state.isMinor, state.allowed]);

  // Pick up wherever this Passport already is.
  useEffect(() => {
    if (loading) return;
    if (state.allowed && state.consented) setStep("done");
    else if (state.verified) setStep("consent");
  }, [loading, state.allowed, state.consented, state.verified]);

  useEffect(() => {
    if (!guardianEmail && user?.email) setGuardianEmail(user.email);
  }, [user?.email, guardianEmail]);

  const allDeclared = declarations.guardian && declarations.notice && declarations.processing;

  const call = async (fn: "parent-verification" | "parental-consent", body: Record<string, unknown>) => {
    const { data, error } = await supabase.functions.invoke(fn, { body });
    if (error) {
      // Edge functions return a readable message in the body on 4xx.
      const ctx = (error as { context?: { json?: () => Promise<{ message?: string }> } }).context;
      let message = error.message;
      try {
        const parsed = await ctx?.json?.();
        if (parsed?.message) message = parsed.message;
      } catch {
        /* keep the transport message */
      }
      throw new Error(message);
    }
    return data as Record<string, unknown>;
  };

  const sendCode = async () => {
    setBusy(true);
    try {
      await call("parent-verification", { action: "send_code", guardian_email: guardianEmail });
      toast.success("Code sent. Check your inbox.");
      setStep("code");
    } catch (e) {
      toast.error((e as Error).message);
    } finally {
      setBusy(false);
    }
  };

  const confirmCode = async () => {
    setBusy(true);
    try {
      await call("parent-verification", {
        action: "confirm_code",
        code,
        guardian_name: guardianName,
      });
      await refresh();
      toast.success("Thank you — you're verified.");
      setStep("consent");
    } catch (e) {
      toast.error((e as Error).message);
    } finally {
      setBusy(false);
    }
  };

  const grantConsent = async () => {
    setBusy(true);
    try {
      await call("parental-consent", {
        action: "grant",
        guardian_declared: declarations.guardian,
        privacy_notice_read: declarations.notice,
        processing_consent: declarations.processing,
        guardian_email: guardianEmail,
      });
      await refresh();
      setStep("done");
    } catch (e) {
      toast.error((e as Error).message);
    } finally {
      setBusy(false);
    }
  };

  const stepIndex = useMemo(
    () => ({ verify: 1, code: 1, consent: 2, done: 3 })[step],
    [step],
  );

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" role="status" aria-live="polite">
        <Loader2 className="h-6 w-6 animate-spin text-primary" aria-hidden="true" />
        <span className="sr-only">Loading</span>
      </div>
    );
  }

  // Adults (or accounts with no birth date) never need this screen.
  if (state.signedIn && !state.isMinor) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center px-4">
        <Card className="w-full max-w-md text-center">
          <CardContent className="pt-10 pb-8 px-6 space-y-5">
            <ShieldCheck className="mx-auto h-10 w-10 text-primary" aria-hidden="true" />
            <h1 className="font-display text-2xl font-bold">You're good to go</h1>
            <p className="text-sm text-muted-foreground">
              Guardian consent is only needed for learners under 18.
            </p>
            <Button className="w-full h-12" onClick={() => navigate(next, { replace: true })}>
              Continue
              <ArrowRight className="h-4 w-4" aria-hidden="true" />
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background px-4 py-8 sm:py-12">
      <div className="mx-auto w-full max-w-lg space-y-6">
        <div className="text-center space-y-2">
          <WeKitLogo size="sm" />
          <p className="text-[11px] uppercase tracking-[0.2em] text-primary font-semibold">
            Step {stepIndex} of 3
          </p>
          <h1 className="font-display text-2xl sm:text-3xl font-bold text-foreground">
            One final step: open {childName}'s Career Passport
          </h1>
          <p className="text-sm text-muted-foreground leading-relaxed">
            Because {childName} is under 18, we verify that you're an adult parent or guardian, and
            ask for your consent, before we begin processing their assessment information.
          </p>
        </div>

        {/* ---------- Step 1: adult verification ---------- */}
        {(step === "verify" || step === "code") && (
          <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}>
            <Card>
              <CardContent className="pt-6 pb-6 px-5 space-y-5">
                <div className="flex items-start gap-3">
                  <ShieldCheck className="mt-0.5 h-5 w-5 shrink-0 text-primary" aria-hidden="true" />
                  <div>
                    <h2 className="font-semibold text-foreground">Verify you're an adult</h2>
                    <p className="text-sm text-muted-foreground">
                      We send a one-time code to your email. We store only the fact that an adult
                      was verified — never any identity documents.
                    </p>
                  </div>
                </div>

                {step === "verify" ? (
                  <div className="space-y-4">
                    <div className="space-y-2">
                      <Label htmlFor="guardian-name">Your full name</Label>
                      <Input
                        id="guardian-name"
                        autoComplete="name"
                        placeholder="Parent or guardian name"
                        value={guardianName}
                        onChange={(e) => setGuardianName(e.target.value)}
                        maxLength={120}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="guardian-email">Your email</Label>
                      <Input
                        id="guardian-email"
                        type="email"
                        autoComplete="email"
                        placeholder="you@example.com"
                        value={guardianEmail}
                        onChange={(e) => setGuardianEmail(e.target.value)}
                        maxLength={255}
                      />
                    </div>
                    <Button
                      className="w-full h-12"
                      onClick={sendCode}
                      disabled={busy || !guardianEmail.trim()}
                    >
                      {busy ? (
                        <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
                      ) : (
                        <Mail className="h-4 w-4" aria-hidden="true" />
                      )}
                      Send my verification code
                    </Button>
                  </div>
                ) : (
                  <div className="space-y-4">
                    <div className="space-y-2">
                      <Label htmlFor="otp">6-digit code</Label>
                      <Input
                        id="otp"
                        inputMode="numeric"
                        autoComplete="one-time-code"
                        placeholder="000000"
                        className="text-center text-2xl tracking-[0.5em]"
                        value={code}
                        onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
                      />
                      <p className="text-xs text-muted-foreground">
                        Sent to {guardianEmail}. It expires in 10 minutes.
                      </p>
                    </div>
                    <Button
                      className="w-full h-12"
                      onClick={confirmCode}
                      disabled={busy || code.length !== 6}
                    >
                      {busy && <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />}
                      Verify &amp; continue
                      <ArrowRight className="h-4 w-4" aria-hidden="true" />
                    </Button>
                    <Button
                      variant="ghost"
                      className="w-full"
                      onClick={() => setStep("verify")}
                      disabled={busy}
                    >
                      Use a different email
                    </Button>
                  </div>
                )}
              </CardContent>
            </Card>
          </motion.div>
        )}

        {/* ---------- Step 2: explicit consent ---------- */}
        {step === "consent" && (
          <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}>
            <Card>
              <CardContent className="pt-6 pb-6 px-5 space-y-5">
                <div>
                  <h2 className="font-display text-xl font-bold text-foreground">
                    You're opening a Passport for {childName}
                  </h2>
                  <p className="mt-2 text-sm text-muted-foreground leading-relaxed">
                    Career Clarity 360™ helps your child explore how they work, what interests
                    them, what matters to them and the futures they could explore.
                  </p>
                </div>

                <div className="rounded-2xl border border-border bg-muted/30 p-4">
                  <p className="text-sm font-semibold text-foreground mb-2">You're consenting to:</p>
                  <ul className="space-y-2 text-sm text-muted-foreground">
                    {[
                      "collection of the learner's assessment responses",
                      "creation of their Career Clarity 360™ Passport",
                      "generation of assessment-derived insights",
                      "use of those insights to provide the WCC360 service",
                      `storage of their Passport and assessment history for ${CONSENT_RETENTION_YEARS} years`,
                    ].map((line) => (
                      <li key={line} className="flex items-start gap-2">
                        <CheckCircle2
                          className="mt-0.5 h-4 w-4 shrink-0 text-primary"
                          aria-hidden="true"
                        />
                        <span>{line}</span>
                      </li>
                    ))}
                  </ul>
                </div>

                <fieldset className="space-y-3">
                  <legend className="sr-only">Guardian declarations</legend>
                  {[
                    {
                      id: "d-guardian",
                      key: "guardian" as const,
                      label: `I confirm that I am the parent or legal guardian of ${childName}.`,
                    },
                    {
                      id: "d-notice",
                      key: "notice" as const,
                      label:
                        "I have read the Privacy Notice and understand how my child's data will be used.",
                    },
                    {
                      id: "d-processing",
                      key: "processing" as const,
                      label:
                        "I give verifiable parental consent for WeKIT to process my child's personal data for Career Clarity 360™.",
                    },
                  ].map((d) => (
                    <div key={d.id} className="flex items-start gap-3">
                      <Checkbox
                        id={d.id}
                        checked={declarations[d.key]}
                        onCheckedChange={(v) =>
                          setDeclarations((prev) => ({ ...prev, [d.key]: v === true }))
                        }
                        className="mt-0.5"
                      />
                      <Label
                        htmlFor={d.id}
                        className="text-sm font-normal leading-relaxed text-muted-foreground cursor-pointer"
                      >
                        {d.label}
                      </Label>
                    </div>
                  ))}
                </fieldset>

                <p className="text-xs text-muted-foreground">
                  You can withdraw consent, export the record or ask us to erase their data at any
                  time from the{" "}
                  <Link to="/passport/guardian" className="underline text-primary">
                    guardian console
                  </Link>
                  .
                </p>

                <Button
                  className="w-full h-12 text-base font-semibold"
                  onClick={grantConsent}
                  disabled={busy || !allDeclared}
                >
                  {busy && <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />}
                  <Sparkles className="h-5 w-5" aria-hidden="true" />
                  Open the Passport
                </Button>
                {!allDeclared && (
                  <p className="text-xs text-muted-foreground text-center" aria-live="polite">
                    All three confirmations are needed before we can begin.
                  </p>
                )}
              </CardContent>
            </Card>
          </motion.div>
        )}

        {/* ---------- Step 3: done ---------- */}
        {step === "done" && (
          <motion.div initial={{ opacity: 0, scale: 0.97 }} animate={{ opacity: 1, scale: 1 }}>
            <Card className="border-primary/20 text-center">
              <CardContent className="pt-10 pb-8 px-6 space-y-5">
                <div className="mx-auto w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center">
                  <CheckCircle2 className="h-8 w-8 text-primary" aria-hidden="true" />
                </div>
                <h2 className="font-display text-2xl font-bold text-foreground">
                  Thank you — {childName}'s Career Passport is open
                </h2>
                <p className="text-sm text-muted-foreground leading-relaxed">
                  Thank you for consenting. It's recorded with a timestamp and a tamper-evident
                  audit trail, and you can review or withdraw it whenever you like.
                </p>
                <Button
                  className="w-full h-12 text-base font-semibold"
                  onClick={() => navigate(next, { replace: true })}
                >
                  Begin the walk
                  <ArrowRight className="h-5 w-5" aria-hidden="true" />
                </Button>
                <Button variant="outline" className="w-full" asChild>
                  <Link to="/passport/guardian">Guardian console</Link>
                </Button>
              </CardContent>
            </Card>
          </motion.div>
        )}

        <p className="flex items-center justify-center gap-2 text-xs text-muted-foreground">
          <Lock className="h-3.5 w-3.5" aria-hidden="true" />
          We keep only the proof that an adult consented — no identity documents.
        </p>
      </div>
    </div>
  );
};

export default PassportConsent;
